import { Zap, Crown, Building2, LucideIcon } from 'lucide-react';

export type PlanFeature =
  | 'job_scheduling'
  | 'gps_tracking'
  | 'route_history'
  | 'customer_app'
  | 'basic_dashboard'
  | 'equipment_tracking'
  | 'google_calendar'
  | 'data_export'
  | 'performance_monitoring'
  | 'multi_location'
  | 'api_access'
  | 'warranty_tracking'
  | 'advanced_analytics'
  | 'audit_log'
  | 'stop_detection'
  | 'document_management'
  | 'certifications'
  | 'ai_receptionist'
  // Estimates: still on every paid tier by intent — it is a reason to switch,
  // not an upsell wall. But it is NOT in any plan's gated_features, because
  // during the rollout access comes from subscriptions.estimates_enabled
  // instead (see usePlanRestrictions and 20260830100000). When the rollout
  // finishes it moves back into the arrays below and the flag goes away.
  | 'estimates'
  | 'ai_estimate_drafting';

export interface PlanFeatureItem {
  /** i18n key under `subscription:plans.<plan>.features.<featureKey>` */
  nameKey: string;
  included: boolean;
  comingSoon?: boolean;
}

export interface PlanConfig {
  id: string;
  name: string;
  displayName: string;
  price: number;
  price_annual: number;
  /** i18n key under `subscription:plans.<plan>.description` */
  descriptionKey: string;
  workers_included: number;
  extra_worker_cost: number;
  photo_limit: number | null;
  features: PlanFeatureItem[];
  gated_features: PlanFeature[];
  popular?: boolean;
  comingSoon?: boolean;
  icon: LucideIcon;
  /** i18n key under `subscription:ctaText.<key>` */
  ctaTextKey: string;
}

const PLANS: PlanConfig[] = [
  {
    id: 'starter',
    name: 'starter',
    displayName: 'Starter',
    price: 129,
    price_annual: 99,
    descriptionKey: 'plans.starter.description',
    workers_included: 2,
    extra_worker_cost: 19,
    photo_limit: 3,
    icon: Zap,
    ctaTextKey: 'ctaText.startFreeTrial',
    gated_features: ['job_scheduling', 'gps_tracking', 'customer_app', 'basic_dashboard'],
    features: [
      { nameKey: 'plans.starter.features.techs', included: true },
      { nameKey: 'plans.starter.features.scheduling', included: true },
      { nameKey: 'plans.starter.features.gps', included: true },
      { nameKey: 'plans.starter.features.customerApp', included: true },
      { nameKey: 'plans.starter.features.dashboard', included: true },
      { nameKey: 'plans.starter.features.email', included: true },
      { nameKey: 'plans.starter.features.routeHistory', included: false },
      { nameKey: 'plans.starter.features.equipment', included: false },
      { nameKey: 'plans.starter.features.googleCalendar', included: false },
    ],
  },
  {
    id: 'professional',
    name: 'professional',
    displayName: 'Professional',
    price: 289,
    price_annual: 229,
    descriptionKey: 'plans.professional.description',
    workers_included: 8,
    extra_worker_cost: 15,
    photo_limit: 10,
    icon: Crown,
    ctaTextKey: 'ctaText.startFreeTrial',
    popular: true,
    gated_features: [
      'job_scheduling', 'gps_tracking', 'route_history', 'customer_app',
      'basic_dashboard', 'equipment_tracking', 'google_calendar', 'data_export',
      'performance_monitoring', 'stop_detection', 'document_management', 'certifications',
      'ai_estimate_drafting',
    ],
    features: [
      { nameKey: 'plans.professional.features.techs', included: true },
      { nameKey: 'plans.professional.features.everythingStarter', included: true },
      { nameKey: 'plans.professional.features.routeHistory', included: true },
      { nameKey: 'plans.professional.features.equipment', included: true },
      { nameKey: 'plans.professional.features.dataExport', included: true },
      { nameKey: 'plans.professional.features.stopDetection', included: true },
      { nameKey: 'plans.professional.features.documentManagement', included: true },
      { nameKey: 'plans.professional.features.prioritySupport', included: true },
      { nameKey: 'plans.professional.features.googleCalendar', included: true },
      { nameKey: 'plans.professional.features.performance', included: true },
    ],
  },
  {
    id: 'business',
    name: 'business',
    displayName: 'Business',
    price: 549,
    price_annual: 449,
    descriptionKey: 'plans.business.description',
    workers_included: 25,
    extra_worker_cost: 12,
    photo_limit: null,
    icon: Building2,
    ctaTextKey: 'ctaText.startFreeTrial',
    // multi_location, api_access, advanced_analytics, audit_log are advertised as
    // "Coming soon" for Business and are NOT granted here yet (see the comingSoon
    // feature items below). The Internal plan still grants them for QA/dogfooding.
    gated_features: [
      'job_scheduling', 'gps_tracking', 'route_history', 'customer_app',
      'basic_dashboard', 'equipment_tracking', 'google_calendar', 'data_export',
      'performance_monitoring', 'warranty_tracking',
      'stop_detection', 'document_management', 'certifications',
      'ai_estimate_drafting',
    ],
    features: [
      { nameKey: 'plans.business.features.techs', included: true },
      { nameKey: 'plans.business.features.everythingProfessional', included: true },
      { nameKey: 'plans.business.features.warranty', included: true },
      { nameKey: 'plans.business.features.chatSupport', included: true },
      { nameKey: 'plans.business.features.advancedAnalytics', included: false, comingSoon: true },
      { nameKey: 'plans.business.features.audit', included: false, comingSoon: true },
      { nameKey: 'plans.business.features.multiLocation', included: false, comingSoon: true },
      { nameKey: 'plans.business.features.api', included: false, comingSoon: true },
    ],
  },
];

// Internal plan for family/bypass users — full access
const INTERNAL_PLAN: PlanConfig = {
  id: 'internal',
  name: 'internal',
  displayName: 'Internal',
  price: 0,
  price_annual: 0,
  descriptionKey: 'plans.internal.description',
  workers_included: 999,
  extra_worker_cost: 0,
  photo_limit: null,
  icon: Crown,
  ctaTextKey: 'ctaText.startFreeTrial',
  gated_features: [
    'job_scheduling', 'gps_tracking', 'route_history', 'customer_app',
    'basic_dashboard', 'equipment_tracking', 'google_calendar', 'data_export',
    'performance_monitoring', 'multi_location', 'api_access', 'warranty_tracking',
    'advanced_analytics', 'audit_log', 'stop_detection', 'document_management', 'certifications',
    'ai_estimate_drafting',
  ],
  features: [],
};

export function canTierUseFeature(tier: string | null, feature: PlanFeature): boolean {
  if (!tier) return false;
  if (tier === 'internal') return true;
  const plan = PLANS.find((p) => p.name === tier);
  if (!plan) return false;
  return plan.gated_features.includes(feature);
}

export function requiredPlanForFeature(feature: PlanFeature): string | null {
  for (const plan of PLANS) {
    if (plan.gated_features.includes(feature)) return plan.displayName;
  }
  return null;
}

export function getPlanConfig(tier: string | null): PlanConfig | null {
  if (!tier) return null;
  if (tier === 'internal') return INTERNAL_PLAN;
  return PLANS.find((p) => p.name === tier) ?? null;
}

const useSubscriptionConfig = () => ({
  plans: PLANS,
  getPlanById: (id: string) => PLANS.find((p) => p.id === id) ?? null,
  getPlanConfig,
  canTierUseFeature,
  requiredPlanForFeature,
});

export default useSubscriptionConfig;
